import { FC, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  Divider,
  H3,
  Icon,
  makeStyles,
  Typography,
} from '@perk-ui/core'
import QRCode from 'qrcode'
import { Check, ExternalLink, Loader, X } from 'react-feather'

import useConsentPolling from '../hooks/useConsentPolling'
import useCreateQRSession from '../hooks/useCreateQRSession'
import useQRSessionTracking from '../hooks/useQRSessionTracking'

type QRState = 'qr' | 'loading' | 'success' | 'error'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        maxWidth: '500px',
        width: '100%',
        borderRadius: '12px',
      },
    },
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  closeButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
  divider: {
    marginBottom: theme.spacing(3),
  },
  description: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  boldText: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  qrCodeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  qrCodeBox: {
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    minWidth: '200px',
  },
  qrCodePlaceholder: {
    color: theme.palette.text.disabled,
    fontSize: '14px',
    textAlign: 'center',
  },
  qrInstruction: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  continueButton: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  timeoutButton: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2, 0),
  },
  loadingSpinner: {
    marginBottom: theme.spacing(3),
    animation: '$spin 1s linear infinite',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2, 0),
  },
  successIcon: {
    backgroundColor: '#4ade80',
    color: 'white',
    borderRadius: '50%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  troubleshootingText: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

interface SurveyCompletionModalProps {
  open: boolean
  onClose: () => void
  surveyUrl: string
  surveyContext: string
  patientId: string
  onContinueInBrowser: () => void
}

const SurveyCompletionModal: FC<SurveyCompletionModalProps> = ({
  open,
  onClose,
  surveyUrl,
  surveyContext,
  patientId,
  onContinueInBrowser,
}) => {
  const classes = useStyles()
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [qrSessionId, setQrSessionId] = useState<string>('')
  const [qrState, setQrState] = useState<QRState>('qr')
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null)
  const [isConsentPolling, setIsConsentPolling] = useState<boolean>(false)
  const sessionCreatedRef = useRef(false)

  // QR session creation hook
  const createQRSession = useCreateQRSession()

  // QR session tracking hook
  const {
    status,
    isLoading: _isLoading,
    error: _error,
    startTracking,
    stopTracking,
  } = useQRSessionTracking(qrSessionId)

  // Consent polling hook
  const _consentPolling = useConsentPolling({
    enabled: isConsentPolling,
    patientId,
    onSuccess: (data) => {
      if (data.consented) {
        // Ensure minimum 3 seconds loading time
        if (loadingStartTime) {
          const elapsed = Date.now() - loadingStartTime
          const remaining = Math.max(0, 3000 - elapsed)

          setTimeout(() => {
            setQrState('success')
            setIsConsentPolling(false)
          }, remaining)
        } else {
          setQrState('success')
          setIsConsentPolling(false)
        }
      }
    },
  })

  // Generate unique session ID and create QR session
  useEffect(() => {
    if (open && !sessionCreatedRef.current) {
      const sessionId = `qr_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`
      setQrSessionId(sessionId)
      setQrState('qr')
      sessionCreatedRef.current = true

      // Patient ID is now passed as prop

      // Create QR session in backend
      createQRSession.mutate({
        sessionId,
        metadata: {
          context: surveyContext,
        },
      })
    } else if (!open) {
      // Clean up when modal closes
      stopTracking()
      setQrSessionId('')
      setQrState('qr')
      setLoadingStartTime(null)
      setIsConsentPolling(false)
      sessionCreatedRef.current = false
    }
  }, [open, surveyContext, surveyUrl])

  // Auto-start tracking when QR modal opens and session ID is ready
  useEffect(() => {
    if (open && qrSessionId && qrState === 'qr') {
      startTracking()
    }
  }, [open, qrSessionId, qrState])

  // Handle QR session status changes with automatic state transitions
  useEffect(() => {
    if (!open) return

    switch (status) {
      case 'scanned':
        setQrState('loading')
        setLoadingStartTime(Date.now())
        // Start consent polling after QR is scanned
        if (patientId) {
          stopTracking()
          setIsConsentPolling(true)
        }
        break
      case 'loaded':
        // This case is now handled by consent polling
        break
      case 'expired':
      case 'error':
        setQrState('error')
        stopTracking()
        setIsConsentPolling(false)
        break
      default:
        // 'pending' state - no action needed
        break
    }
  }, [status, open]) // Stable dependencies only

  useEffect(() => {
    if (open && surveyUrl && qrSessionId) {
      // Modify URL to include QR tracking session ID
      const trackingUrl = surveyUrl.replace(
        'comm_channel=manual',
        `comm_channel=qr&qr_session_id=${qrSessionId}`,
      )

      // Console log for testing - remove after backend integration is verified
      // eslint-disable-next-line no-console
      // console.log('🔗 QR Test URL:', trackingUrl)

      QRCode.toDataURL(trackingUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((dataUrl) => {
          setQrCodeDataUrl(dataUrl)
        })
        .catch((error) => {
          console.error('Error generating QR code:', error)
        })
    }
  }, [open, surveyUrl, qrSessionId])

  const handleContinueInBrowser = (event?: React.MouseEvent) => {
    // Prevent event bubbling that could cause unintended navigation
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    try {
      stopTracking()
      setIsConsentPolling(false)

      // Call the parent's continue in browser function to open survey
      onContinueInBrowser()

      // Small delay to ensure browser opens before modal closes
      setTimeout(() => {
        onClose()
      }, 150)
    } catch (error) {
      console.error('Error in handleContinueInBrowser:', error)
      // Fallback: just close the modal if there's an error
      onClose()
    }
  }

  const handleTryAgain = (event?: React.MouseEvent) => {
    // Prevent event bubbling that could cause unintended navigation
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    try {
      // Reset all QR-related state to start fresh
      stopTracking()
      setIsConsentPolling(false)

      // Reset to QR state to show QR code again
      setQrState('qr')

      // Don't close modal - user wants to try QR again
    } catch (error) {
      console.error('Error in handleTryAgain:', error)
      // If there's an error, at least reset to QR state
      setQrState('qr')
    }
  }

  const handleReturnToPatientList = () => {
    stopTracking()
    setIsConsentPolling(false)
    onClose()
    // TODO: Add navigation to patient list
  }

  const handleModalClose = () => {
    stopTracking()
    setIsConsentPolling(false)
    onClose()
  }

  // Patient ID is now passed as prop - no need to extract from URL

  const renderContent = () => {
    switch (qrState) {
      case 'qr':
        return (
          <>
            <div className={classes.titleRow}>
              <H3>Start Survey on a Mobile Device</H3>
              <Button
                size="small"
                variant="text"
                onClick={handleModalClose}
                className={classes.closeButton}
              >
                <Icon size="medium" color="secondaryText">
                  <X />
                </Icon>
              </Button>
            </div>
            <Divider className={classes.divider} />

            <Typography className={classes.description}>
              Scan the QR code below to open the survey on a mobile or tablet.
            </Typography>

            <Typography className={classes.description}>
              Once it&apos;s open, you can hand the device to the patient to
              complete the{' '}
              <span className={classes.boldText}>{surveyContext}</span> survey
              securely.
            </Typography>

            <div className={classes.qrCodeContainer}>
              <div className={classes.qrCodeBox}>
                {qrCodeDataUrl ? (
                  <img
                    src={qrCodeDataUrl}
                    alt="QR Code for survey"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                ) : (
                  <div className={classes.qrCodePlaceholder}>
                    Generating QR Code...
                  </div>
                )}
              </div>

              <Typography className={classes.qrInstruction}>
                Scan this QR code to continue on a mobile device
              </Typography>

              <Typography className={classes.troubleshootingText}>
                Once scanned, we&apos;ll automatically detect the survey opening
                and guide you through the process.
              </Typography>
            </div>

            <div className={classes.buttonContainer}>
              <Button
                variant="text"
                onClick={handleContinueInBrowser}
                className={classes.continueButton}
                endIcon={<ExternalLink size={16} />}
              >
                Prefer to stay here? Click to continue {surveyContext} survey in
                this browser
              </Button>
            </div>
          </>
        )

      case 'loading':
        return (
          <>
            <div className={classes.titleRow}>
              <H3>Opening Survey on Another Device...</H3>
              <Button
                size="small"
                variant="text"
                onClick={handleModalClose}
                className={classes.closeButton}
              >
                <Icon size="medium" color="secondaryText">
                  <X />
                </Icon>
              </Button>
            </div>
            <Divider className={classes.divider} />

            <div className={classes.loadingContainer}>
              <Loader
                size={48}
                className={classes.loadingSpinner}
                color="#3b82f6"
              />

              <Typography className={classes.description}>
                Hang tight — we&apos;re sending the survey to your device now.
              </Typography>

              <Typography className={classes.troubleshootingText}>
                If nothing happens, make sure the QR code was scanned
                successfully and the device is connected to the internet.
              </Typography>

              <div className={classes.actionButtons}>
                <Button
                  variant="text"
                  onClick={handleTryAgain}
                  className={classes.timeoutButton}
                >
                  Try Again
                </Button>
                <Button
                  variant="text"
                  onClick={handleContinueInBrowser}
                  className={classes.timeoutButton}
                >
                  Open in Browser
                </Button>
              </div>
            </div>
          </>
        )

      case 'success':
        return (
          <>
            <div className={classes.titleRow}>
              <H3>Survey Opened on Mobile Device</H3>
              <Button
                size="small"
                variant="text"
                onClick={handleModalClose}
                className={classes.closeButton}
              >
                <Icon size="medium" color="secondaryText">
                  <X />
                </Icon>
              </Button>
            </div>
            <Divider className={classes.divider} />

            <div className={classes.successContainer}>
              <div className={classes.successIcon}>
                <Check size={32} />
              </div>

              <Typography className={classes.description}>
                The survey is now open on the other device. You can hand the
                device to the patient while continuing your workflow here in the
                browser.
              </Typography>

              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  onClick={handleReturnToPatientList}
                  className={classes.primaryButton}
                >
                  Return to Patient List
                </Button>
              </div>
            </div>
          </>
        )

      case 'error':
        return (
          <>
            <div className={classes.titleRow}>
              <H3>Unable to Open Survey</H3>
              <Button
                size="small"
                variant="text"
                onClick={handleModalClose}
                className={classes.closeButton}
              >
                <Icon size="medium" color="secondaryText">
                  <X />
                </Icon>
              </Button>
            </div>
            <Divider className={classes.divider} />

            <div className={classes.loadingContainer}>
              <Typography className={classes.description}>
                We weren&apos;t able to detect that the survey opened on your
                mobile device.
              </Typography>

              <Typography className={classes.troubleshootingText}>
                This could be due to network issues or the QR code not being
                scanned properly.
              </Typography>

              <div className={classes.actionButtons}>
                <Button
                  variant="contained"
                  onClick={handleTryAgain}
                  className={classes.primaryButton}
                >
                  Try Again
                </Button>
                <Button
                  variant="text"
                  onClick={handleContinueInBrowser}
                  className={classes.timeoutButton}
                >
                  Open in Browser
                </Button>
              </div>
            </div>
          </>
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={handleModalClose} className={classes.root}>
      <Box p={4}>{renderContent()}</Box>
    </Dialog>
  )
}

export default SurveyCompletionModal
